'use client';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountError, useBlockPart, useCommerceSource, tx, PortalLink } from '@akademi/site-builder-runtime';
import { AdapterPlaceholder, BlockRoot, sharedStyles } from '@akademi/site-builder-blocks';
import {
  buttonStyle,
  errorStyle,
  leadStyle,
  linkRowStyle,
  noticeStyle,
  titleStyle,
  useAccountErrorText,
} from './accountShared';

const { sectionInner } = sharedStyles;

type Localized = string | Record<string, string>;

/**
 * Where the change-of-email link lands.
 *
 * <p>The change happens when the shopper presses the button, not when the page
 * loads. Mail scanners open links before the recipient does, and a change made
 * on that first visit would be made by the scanner — and spend the link before
 * its owner ever saw it.
 *
 * <p>The token leaves the address bar as soon as it is read, as on the reset
 * page, so it is neither kept in history nor sent on in a `Referer`. A change
 * signs the account out everywhere, so the page ends at "sign in".
 */
export interface ConfirmEmailChangeProps {
  title?: Localized;
  intro?: Localized;
  signInHref?: string;
  accountHref?: string;
}

export function ConfirmEmailChange({
  title,
  intro,
  signInHref = '/sign-in',
  accountHref = '/account',
}: ConfirmEmailChangeProps) {
  const commerce = useCommerceSource();
  const part = useBlockPart();
  const { t } = useTranslation('site-builder-blocks');
  const errorText = useAccountErrorText();

  const [token, setToken] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [spent, setSpent] = useState(false);

  useEffect(() => {
    const url = new URL(window.location.href);
    const found = url.searchParams.get('token');
    if (found) {
      setToken(found);
      url.searchParams.delete('token');
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    }
    setReady(true);
  }, []);

  if (!commerce?.confirmEmailChange) {
    return (
      <BlockRoot as="section">
        <div style={sectionInner}>
          <AdapterPlaceholder
            blockType="ConfirmEmailChange"
            adapterName="CommerceSource"
            reason={t('commerce.account.noAdapter')}
          />
        </div>
      </BlockRoot>
    );
  }

  async function confirm() {
    if (!commerce?.confirmEmailChange || !token || busy) return;
    setBusy(true);
    setError(null);
    try {
      await commerce.confirmEmailChange(token);
      setDone(true);
    } catch (failure) {
      // A spent or expired link will not work on a second press either.
      setSpent(failure instanceof AccountError && failure.kind === 'INVALID_LINK');
      setError(errorText(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <BlockRoot as="section">
      <div style={{ ...sectionInner, maxWidth: 520 }}>
        <h1 {...part('title')} style={titleStyle}>
          {tx(title) || t('commerce.account.emailChange.confirmTitle')}
        </h1>
        {done ? (
          <>
            <p role="status" style={noticeStyle}>
              {t('commerce.account.emailChange.confirmed')}
            </p>
            <div style={linkRowStyle}>
              <PortalLink href={signInHref}>{t('commerce.account.signIn')}</PortalLink>
            </div>
          </>
        ) : ready && !token ? (
          <>
            <p role="alert" style={errorStyle}>
              {t('commerce.account.errors.INVALID_LINK')}
            </p>
            <div style={linkRowStyle}>
              <PortalLink href={accountHref}>{t('commerce.account.goToAccount')}</PortalLink>
            </div>
          </>
        ) : (
          <div style={{ display: 'grid', gap: 16 }}>
            <p {...part('intro')} style={{ ...leadStyle, margin: 0 }}>
              {tx(intro) || t('commerce.account.emailChange.confirmIntro')}
            </p>
            {error && (
              <p role="alert" style={errorStyle}>
                {error}
              </p>
            )}
            {spent ? (
              <div style={linkRowStyle}>
                <PortalLink href={accountHref}>{t('commerce.account.goToAccount')}</PortalLink>
              </div>
            ) : (
              <button type="button" onClick={() => void confirm()} disabled={busy || !token} style={buttonStyle}>
                {busy ? t('commerce.account.emailChange.confirming') : t('commerce.account.emailChange.confirm')}
              </button>
            )}
          </div>
        )}
      </div>
    </BlockRoot>
  );
}
